import {
  Checkbox,
  InfoTooltip,
  Pill,
  SearchField,
  ToggleButton,
} from '@wandb/components';
import React, {useMemo, useState} from 'react';
import {twMerge} from 'tailwind-merge';

import {useDebounceState} from '../../../util/hooks';
import {useMaxPinnedRuns} from '../../../util/rampFeatureFlags/rampFeatureFlags';
import {toSorted} from '../../../util/toSorted';
import {maybePluralize} from '../../../util/uihelpers';
import {usePinnedRunIdsOnly} from '../RunSelectorRunSetContext';
import {type ProjectOption} from './useCrossProjectOptions';
import {useCrossProjectRuns} from './useCrossProjectRuns';

type RunsListProps = {
  selectedProject: ProjectOption;
  selectedRunIds: Set<string>;
  onToggleRun: (runId: string) => void;
};

export const RunsList = ({
  selectedProject,
  selectedRunIds,
  onToggleRun,
}: RunsListProps) => {
  const pinnedRunIds = usePinnedRunIdsOnly();
  const pinnedRunIdsSet = useMemo(() => new Set(pinnedRunIds), [pinnedRunIds]);
  const maxPinnedRuns = useMaxPinnedRuns();
  const remainingSlots = maxPinnedRuns - selectedRunIds.size;
  const isMaxSelected = remainingSlots <= 0;

  const [isRegexSearch, setIsRegexSearch] = useState(true);
  const [search, debouncedSearch, setSearch] = useDebounceState('', 300);
  const {loading, runs, totalCount} = useCrossProjectRuns(
    selectedProject.internalId,
    debouncedSearch,
    isRegexSearch
  );

  // List already pinned runs first
  const sortedRuns = useMemo(
    () =>
      toSorted(runs, (a, b) => {
        const aPinned = pinnedRunIdsSet.has(a.id) ? 0 : 1;
        const bPinned = pinnedRunIdsSet.has(b.id) ? 0 : 1;
        return aPinned - bPinned;
      }),
    [runs, pinnedRunIdsSet]
  );

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-8">
      <div className="shrink-0 font-semibold flex items-center gap-4">
        <span aria-describedby="select-runs-tooltip">Select runs</span>
        <InfoTooltip id="select-runs-tooltip" aria-label="Runs limit info">
          You can select up to{' '}
          {maybePluralize(Math.max(0, remainingSlots), 'more run')}. Selected
          runs are pinned to your workspace, which allows up to {maxPinnedRuns}{' '}
          pinned runs.
        </InfoTooltip>
      </div>
      <div className="shrink-0 flex gap-8">
        <SearchField
          aria-label="Search runs by name or ID"
          placeholder="Search runs by name or ID"
          value={search}
          onChange={setSearch}
          fullWidth
        />
        <ToggleButton
          aria-label="Toggle regex matching"
          variant="secondary"
          icon="regex"
          isSelected={isRegexSearch}
          onPress={() => setIsRegexSearch(prev => !prev)}
          tooltip={{
            children: `Regex matching is ${isRegexSearch ? 'enabled' : 'disabled'}`,
            placement: 'top end',
          }}
        />
      </div>
      {loading && <p className="text-moon-500">Loading runs...</p>}
      {!loading && runs.length === 0 && (
        <p className="text-moon-500">No runs found</p>
      )}
      {!loading && runs.length > 0 && (
        <>
          <ul className="flex min-h-0 flex-1 flex-col overflow-y-auto">
            {sortedRuns.map(run => {
              const isAlreadyPinned = pinnedRunIdsSet.has(run.id);
              const isChecked = selectedRunIds.has(run.id);
              const isMarkedForUnpin = isAlreadyPinned && !isChecked;
              const isDisabled =
                !isChecked && !isAlreadyPinned && isMaxSelected;
              return (
                <li
                  key={run.id}
                  className="flex shrink-0 items-center gap-8 px-8 py-6 hover:bg-moon-100 rounded">
                  <Checkbox
                    id={`checkbox-${run.name}`}
                    aria-label={`Select run ${run.displayName || run.name}`}
                    isSelected={isChecked}
                    isDisabled={isDisabled}
                    onPress={() => onToggleRun(run.id)}
                  />
                  <label
                    className={twMerge(
                      'truncate text-moon-800 w-full',
                      !isDisabled && 'cursor-pointer'
                    )}
                    htmlFor={`checkbox-${run.name}`}>
                    {run.displayName || run.name}
                  </label>
                  {isAlreadyPinned && (
                    <Pill
                      icon="checkmark2"
                      textValue="Pinned"
                      color={isMarkedForUnpin ? 'moon' : 'green'}
                      tooltip={{
                        children:
                          'This run is currently pinned in the workspace.',
                        placement: 'left',
                      }}
                    />
                  )}
                </li>
              );
            })}
          </ul>
          {totalCount > runs.length && (
            <p className="shrink-0 text-moon-500 text-sm">
              Showing {runs.length} of {totalCount.toLocaleString()} runs. Use
              the search bar to narrow results.
            </p>
          )}
        </>
      )}
    </div>
  );
};
